// pages/overlay/Main.jsx
// The CALM show scene — routed at /overlay/main. Rebuilt 2026-09-27 as the
// quiet sibling of Booth: for entertainment-first events (brunches, parties —
// ~95% custom requests) where the flash board, rotating promo cards and
// marquee are noise. The griddle is the show; the overlay just narrates it.
//
// What's deliberately NOT here (vs Booth / the old stream-era Main):
//   no flash grid, no marquee, no Gazette spotlight, no face-cam window, no
//   particles, no card shimmer, no press strip, no "🥞 LIVE" badge. If you
//   want any of those, Booth is the busy scene — keep this one calm.
//
// Landscape (1920×1080):
//   ┌─ logo · event name · in queue / est. wait ───────────────────┐  96px
//   │                                               │ Now drawing  │
//   │  griddle (transparent — camera sits behind)   │ Cooking ×≤3  │
//   │  ordering QR (SummonBanner) lives here        │ Just done    │
//   │                                               │  (or idle)   │
//   └───────────────────────────────────────────────┴──────────────┘
// Portrait (1080×1920) — same top bar + status rail as Booth, so a
// Booth↔Main scene cut doesn't move the rail and portrait alerts
// (AlertToast PORTRAIT_POS) land in the same spot on both:
//   top bar 108px / StatusRow max(150px, 11vh) / griddle (flex remainder,
//   ≈1080×1520 at y≈322) / URL footer 72px.
//
// Logo: the /jop_stream.png pancake stack has a flat cut edge at exactly
// 144/180 = 0.8 of the image height (measured). The logo bottom-aligns in the
// bar and drops by 0.2·logoH so that cut edge rests ON the gold bottom
// border; the script descenders hang over it, which is the intended look.
//
// Pop-ups kept (they ARE the entertainment): "Now drawing" title card,
// flip countdown, and the tip-jar card — the tip card is suppressed in party
// voice (qrFlows.mode "free": a client-paid event shows no commerce words).
//
// Perf: opacity-only card entrances, no infinite animations. The 1Hz timer
// tick is the only interval.

import { useEffect, useRef, useState } from "react";
import useFlashDesigns from "./../../hooks/useFlashDesigns";
import useOverlayPalette from "./../../hooks/useOverlayPalette";
import useOrientation from "./../../hooks/useOrientation";
import useStreamConfig from "./../../hooks/useStreamConfig";
import useCurrentShiftOrders from "./../../hooks/useCurrentShiftOrders";
import SummonBanner from "./../../components/overlay/SummonBanner";
import QueueCard from "./../../components/overlay/QueueCard";
import StatusRow from "./../../components/overlay/StatusRail";
import JoPLogo from "./../../components/overlay/JoPLogo";
import {
  computeQueueWait,
  fmtTimer,
  fmtWait,
  getRefSrc,
} from "./../../utils/overlayFormat";
import { computeDisplayTitle } from "./../../utils/meditations";

// ── CONSTANTS ─────────────────────────────────────────────────────────────────

const BRAND = {
  gold:   "var(--jop-gold)",
  sky:    "var(--jop-highlight-blue)",
  pink:   "var(--jop-pink)",
  cream:  "var(--jop-cream)",
};
const D = "var(--jop-display)";
const BAR_BG = "linear-gradient(90deg, var(--jop-grad-1) 0%, var(--jop-grad-2) 60%, var(--jop-grad-3) 100%)";

// /jop_stream.png: flat cut edge of the pancake stack at 144/180 of the image.
const LOGO_CUT = 0.8;

// ── STAT — number over label ─────────────────────────────────────────────────
function Stat({ value, label, color, portrait }) {
  return (
    <div style={{ textAlign: "center", flexShrink: 0 }}>
      <div style={{
        color, fontFamily: D, fontWeight: 900, lineHeight: 1,
        fontSize: portrait ? "3.1rem" : "2.6rem",
        fontVariantNumeric: "tabular-nums",
      }}>{value}</div>
      <div style={{
        color: "rgba(255,255,255,0.75)", fontFamily: D, fontWeight: 700,
        fontSize: portrait ? "1.2rem" : "1rem",
        textTransform: "uppercase", letterSpacing: "0.08em", marginTop: "3px",
      }}>{label}</div>
    </div>
  );
}

// ── TOP BAR — logo · event name · queue stats (both orientations) ────────────
function TopBar({ activeShift, queueDepth, waitMin, portrait }) {
  const barH = portrait ? 108 : 96;
  const logoH = Math.round(barH * 1.2);
  const isOpen = !!activeShift?.isQueueOpen;
  return (
    <div style={{
      ...(portrait
        ? { position: "relative", flexShrink: 0 }
        : { position: "absolute", top: 0, left: 0, right: 0 }),
      // content-box: the row is exactly barH tall and the 3px gold border
      // sits BELOW it — the logo math below depends on that.
      height: `${barH}px`, boxSizing: "content-box",
      background: BAR_BG,
      borderBottom: `3px solid ${BRAND.gold}`,
      display: "flex", alignItems: "center",
      padding: portrait ? "0 2rem 0 1.2rem" : "0 2.2rem 0 1.6rem",
      gap: "1.6rem",
      fontFamily: D,
      // Above the status rail so the logo's descenders drape over it.
      zIndex: 4,
      overflow: "visible",
    }}>
      <JoPLogo
        height={logoH}
        style={{
          alignSelf: "flex-end",
          marginBottom: `-${Math.round(logoH * (1 - LOGO_CUT))}px`,
          flexShrink: 0,
        }}
      />

      <div style={{
        flex: 1, minWidth: 0, overflow: "hidden",
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        overflowWrap: "anywhere",
        textAlign: "center",
        color: BRAND.cream, fontWeight: 700, opacity: 0.92,
        fontSize: portrait ? "1.5rem" : "1.6rem",
        lineHeight: 1.15, letterSpacing: "0.04em",
      }}>
        {activeShift?.eventName || ""}
      </div>

      {isOpen ? (
        <div style={{ display: "flex", alignItems: "center", gap: "1.8rem", flexShrink: 0 }}>
          <Stat value={queueDepth} label="in queue" color="var(--jop-slot-cooking)" portrait={portrait} />
          <div style={{ width: "2px", height: portrait ? "60px" : "52px", background: "rgba(255,255,255,0.18)" }} />
          <Stat value={fmtWait(waitMin)} label="est. wait" color="var(--jop-slot-done)" portrait={portrait} />
        </div>
      ) : (
        <div style={{
          flexShrink: 0, textAlign: "center",
          color: BRAND.pink, fontWeight: 900,
          fontSize: portrait ? "2.1rem" : "1.9rem",
          letterSpacing: "0.06em", lineHeight: 1.1,
        }}>
          QUEUE CLOSED
          <div style={{
            color: "rgba(255,255,255,0.55)", fontWeight: 600,
            fontSize: portrait ? "1rem" : "0.95rem",
            letterSpacing: "0.04em", marginTop: "3px", textTransform: "uppercase",
          }}>Thanks for coming!</div>
        </div>
      )}
    </div>
  );
}

// ── IDLE CARD — right column when nothing is on the griddle ──────────────────
// Voice-neutral on purpose: no prices, no "ticket" — the SummonBanner QR on
// the feed carries the how-to-order story.
function IdleCard() {
  return (
    <div style={{
      background: "linear-gradient(135deg, rgba(var(--jop-gold-rgb),0.15) 0%, rgba(var(--jop-orange-rgb),0.12) 50%, rgba(var(--jop-pink-rgb),0.1) 100%)",
      border: "2px solid rgba(var(--jop-gold-rgb),0.4)",
      borderRadius: "16px",
      padding: "1.8rem 1.5rem",
      textAlign: "center",
      fontFamily: D,
    }}>
      <div style={{ fontSize: "3.2rem", lineHeight: 1 }}>🥞</div>
      <div style={{
        color: BRAND.gold, fontSize: "2.1rem", fontWeight: 900,
        letterSpacing: "0.03em", lineHeight: 1.15, marginTop: "0.6rem",
      }}>The griddle's warm</div>
      <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "1.35rem", fontWeight: 600, marginTop: "0.4rem" }}>
        What should we draw next?
      </div>
    </div>
  );
}

// Opacity-only entrance for the right-column cards (no 3D rotate, no shimmer).
function CardIn({ children }) {
  return (
    <div style={{ animation: "jopMainCardIn 0.45s ease-out both", borderRadius: "14px", overflow: "hidden" }}>
      {children}
    </div>
  );
}

// ── FLIP COUNTDOWN — "HERE WE GO!" → 3… 2… 1… FLIP! ─────────────────────────
// Time-based: derives current phase from elapsed time since the flip was triggered.
// When flippedAt (Firestore server timestamp) is available, uses it as t=0 so the
// overlay syncs with the artist view's countdown even if the snapshot arrived late.
const FLIP_PHASES = [
  { end: 2000, id: "primer" },  // 2s primer absorbs network sync gap
  { end: 2900, id: 3 },
  { end: 3800, id: 2 },
  { end: 4700, id: 1 },
  { end: 6100, id: "flip" },
];
function getFlipPhase(elapsed) {
  for (const p of FLIP_PHASES) {
    if (elapsed < p.end) return p.id;
  }
  return "done";
}

function FlipAnimation({ onDone, flippedAt }) {
  const r = useRef(onDone); r.current = onDone;
  // Use flippedAt as the shared clock reference when available, otherwise fall
  // back to mount time (standalone / no Firestore timestamp).
  const t0Ref = useRef(
    flippedAt?.toMillis ? flippedAt.toMillis() : Date.now()
  );
  const [phase, setPhase] = useState(() => getFlipPhase(Date.now() - t0Ref.current));

  useEffect(() => {
    const id = setInterval(() => {
      const elapsed = Date.now() - t0Ref.current;
      const next = getFlipPhase(elapsed);
      if (next === "done") {
        clearInterval(id);
        r.current();
      } else {
        setPhase((prev) => (prev !== next ? next : prev));
      }
    }, 50); // 50ms poll — smooth phase transitions
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 50,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      pointerEvents: "none",
    }}>
      {phase === "primer" ? (
        <div key="primer" style={{
          color: "var(--jop-gold)",
          fontSize: "5rem",
          fontWeight: 900,
          textAlign: "center",
          letterSpacing: "0.06em",
          textShadow: "0 0 40px rgba(var(--jop-gold-rgb),0.5), 0 4px 12px rgba(0,0,0,0.5)",
          animation: "jopCountPop 0.8s cubic-bezier(0.34,1.56,0.64,1) forwards",
        }}>
          HERE WE GO!
        </div>
      ) : typeof phase === "number" && phase > 0 ? (() => {
        const countColors = {
          3: { c: "var(--jop-highlight-blue)", glow: "rgba(var(--jop-highlight-blue-rgb), 0.53)" },
          2: { c: "var(--jop-pink)", glow: "rgba(var(--jop-pink-rgb), 0.53)" },
          1: { c: "var(--jop-gold)", glow: "rgba(var(--jop-gold-rgb), 0.53)" },
        };
        const cc = countColors[phase] || countColors[1];
        return (
          <div key={phase} style={{
            fontSize: "12rem", fontWeight: 900, lineHeight: 1,
            color: cc.c,
            textShadow: `0 0 40px ${cc.glow}, 0 4px 12px rgba(0,0,0,0.5)`,
            animation: "jopCountPop 0.7s cubic-bezier(0.34,1.56,0.64,1) forwards",
          }}>{phase}</div>
        );
      })() : (
        <div key="flip" style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          animation: "jopFlipBurst 1s cubic-bezier(0.34,1.2,0.64,1) forwards",
        }}>
          <div style={{ fontSize: "8rem" }}>🥞</div>
          <div style={{
            fontWeight: 900, fontSize: "3.5rem", marginTop: "0.5rem",
            display: "flex", gap: "0.3em",
          }}>
            {"FLIP!".split("").map((ch, i) => {
              const letters = [
                { c: "var(--jop-highlight-blue)", glow: "rgba(var(--jop-highlight-blue-rgb), 0.53)" },
                { c: "var(--jop-pink)", glow: "rgba(var(--jop-pink-rgb), 0.53)" },
                { c: "var(--jop-gold)", glow: "rgba(var(--jop-gold-rgb), 0.53)" },
                { c: "#b9d550", glow: "rgba(185, 213, 80, 0.53)" },
                { c: "var(--jop-navy)", glow: "rgba(var(--jop-navy-rgb), 0.53)" },
              ];
              const rotates = [-4, 3, -2, 5, -3];
              return (
                <span key={i} style={{
                  color: letters[i].c,
                  textShadow: `0 0 25px ${letters[i].glow}, 0 4px 12px rgba(0,0,0,0.5)`,
                  display: "inline-block",
                  transform: `rotate(${rotates[i]}deg)`,
                }}>{ch}</span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── NOW-DRAWING CARD — the pancake's title in lights ─────────────────────────
// Fires when a new pancake starts drawing; 3.5s auto-dismiss. Shows the
// display TITLE ("Saturnina (#6)", "Untitled #45", the guest's piece title) —
// so the header says what it is, "Now drawing", not "Now creating for".
function CustomerSpotlight({ order }) {
  const title = computeDisplayTitle(order) || "something special";
  const typeLabel = order.isFreestyle ? "FREESTYLE"
    : order.orderType === "magic" ? "MAGIC"
    : order.orderType === "flash" ? "FLASH"
    : "CUSTOM";

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 35,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      pointerEvents: "none",
      animation: "jopSpotlightFade 3.5s ease-in-out forwards",
    }}>
      <style>{`
        @keyframes jopSpotlightFade {
          0%   { opacity: 0; transform: scale(0.8); }
          12%  { opacity: 1; transform: scale(1); }
          80%  { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.95); }
        }
      `}</style>
      <div style={{
        background: "rgba(var(--jop-ink-rgb),0.88)",
        border: "var(--jop-card-border)",
        boxShadow: "0 0 40px rgba(var(--jop-gold-rgb),0.3), 0 0 80px rgba(var(--jop-gold-rgb),0.1)",
        borderRadius: "24px",
        padding: "2rem 3.5rem",
        textAlign: "center",
        maxWidth: "min(760px, 90vw)",
      }}>
        <div style={{
          color: "var(--jop-gold)", fontFamily: D,
          fontSize: "1.6rem", fontWeight: 700, letterSpacing: "0.03em",
        }}>✨ Now drawing ✨</div>
        <div style={{
          color: "white", fontFamily: D,
          fontSize: "3.5rem", fontWeight: 700, lineHeight: 1.1,
          marginTop: "0.4rem", overflowWrap: "anywhere",
          textShadow: "0 2px 16px rgba(var(--jop-gold-rgb),0.4)",
        }}>{title}</div>
        <div style={{
          marginTop: "0.8rem",
          display: "inline-block",
          background: "rgba(var(--jop-gold-rgb),0.15)",
          border: "1px solid rgba(var(--jop-gold-rgb),0.35)",
          borderRadius: "999px",
          padding: "0.3rem 1.2rem",
          color: "var(--jop-cream)", fontFamily: D,
          fontSize: "1rem", fontWeight: 600, letterSpacing: "0.1em",
        }}>{typeLabel}</div>
      </div>
    </div>
  );
}

// ── TIP JAR PROMPT — fires 2s after a pancake hits ready_to_deliver ─────────
function TipJarPrompt({ onDone }) {
  const r = useRef(onDone); r.current = onDone;
  useEffect(() => {
    const t = setTimeout(() => r.current(), 2900);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 40,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      background: "rgba(var(--jop-ink-rgb),0.6)",
      animation: "jopTipFade 2.9s ease-in-out forwards",
    }}>
      <div style={{ fontSize: "6rem", marginBottom: "1rem" }}>🫙</div>
      <div style={{
        color: BRAND.gold, fontWeight: 900, fontSize: "2.5rem",
        letterSpacing: "0.05em", textAlign: "center", lineHeight: 1.3,
      }}>Enjoying the show?<br /><span style={{ color: "white" }}>We FLIP for tips!</span></div>
      <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "1.3rem", marginTop: "1rem" }}>
        thejoyofpancakes.com
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function Main() {
  useOverlayPalette();
  const orientation = useOrientation();
  const portrait = orientation === "portrait";
  const streamConfig = useStreamConfig();
  const {
    activeShift,
    shiftLoaded,
    drawing: drawingOrder,
    cooking: cookingOrder,
    cookingAll,
    queued: queuedOrders,
    allActive,
    recentlyDone,
    transitions,
    clearTransition,
    concurrentCount,
  } = useCurrentShiftOrders();
  // Per-event flash boards still resolve so a FLASH order's card shows its
  // design art — the scene just doesn't display the flash board itself.
  const { designMap } = useFlashDesigns(activeShift);

  const [showFlip, setShowFlip] = useState(false);
  const [flipTimestamp, setFlipTimestamp] = useState(null); // Firestore Timestamp for sync
  const [showTipJar, setShowTipJar] = useState(false);
  const [showSpotlight, setShowSpotlight] = useState(null); // order object or null
  const [now, setNow] = useState(Date.now());
  const [lastDoneOrder, setLastDoneOrder] = useState(null);
  const tipJarTimerRef = useRef(null);
  const doneDismissedRef = useRef(null);
  const prevDrawingIdRef = useRef(null);

  // Party voice (client-paid event) never shows a tip ask — no commerce words.
  const partyVoice = activeShift?.qrFlows?.mode === "free";

  // Live clock — 1Hz tick so the landscape cards' timers count in real time.
  // (Portrait's StatusRow runs its own scoped tick.)
  useEffect(() => {
    if (portrait) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [portrait]);

  // React to transitions reported by useCurrentShiftOrders
  useEffect(() => {
    if (transitions.flippedNow) {
      setFlipTimestamp(transitions.flippedAt);
      setShowFlip(true);
      clearTransition("flippedNow");
    }
    if (transitions.readyToDeliverNow) {
      clearTimeout(tipJarTimerRef.current);
      if (!partyVoice) {
        tipJarTimerRef.current = setTimeout(() => setShowTipJar(true), 2000);
      }
      clearTransition("readyToDeliverNow");
    }
  }, [transitions, clearTransition, partyVoice]);

  useEffect(() => () => clearTimeout(tipJarTimerRef.current), []);

  // Now-drawing card — fires when a new drawing order appears
  useEffect(() => {
    const newId = drawingOrder?.id ?? null;
    if (newId && newId !== prevDrawingIdRef.current) {
      setShowSpotlight(drawingOrder);
      const t = setTimeout(() => setShowSpotlight(null), 3500);
      prevDrawingIdRef.current = newId;
      return () => clearTimeout(t);
    }
    if (!newId) prevDrawingIdRef.current = null;
  }, [drawingOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Done card — show latest finished pancake, but don't re-surface a dismissed one.
  // Reset dismissal when shift changes.
  useEffect(() => {
    doneDismissedRef.current = null;
  }, [activeShift?.id]);

  useEffect(() => {
    const latest = recentlyDone[0] ?? null;
    if (latest && latest.id !== doneDismissedRef.current) {
      setLastDoneOrder(latest);
    } else if (!latest) {
      setLastDoneOrder(null);
    }
  }, [recentlyDone]);

  // When shift fully closes (becomes null), linger done card for 15s then clear
  useEffect(() => {
    if (activeShift) return;
    const t = setTimeout(() => {
      setLastDoneOrder(null);
      doneDismissedRef.current = null;
    }, 15000);
    return () => clearTimeout(t);
  }, [activeShift]);

  // Auto-clear done card: when queue closed AND no more active orders (show is over)
  useEffect(() => {
    if (!activeShift) return;
    const queueClosed = activeShift.isQueueOpen === false;
    const noActiveOrders = !drawingOrder && cookingAll.length === 0;
    if (queueClosed && noActiveOrders && lastDoneOrder) {
      const t = setTimeout(() => {
        doneDismissedRef.current = lastDoneOrder.id;
        setLastDoneOrder(null);
      }, 15000);
      return () => clearTimeout(t);
    }
  }, [activeShift?.isQueueOpen, drawingOrder, cookingOrder, lastDoneOrder?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!shiftLoaded) return null;

  const waitMin = computeQueueWait(allActive, activeShift);
  const queueDepth = [drawingOrder, ...cookingAll, ...queuedOrders].filter(Boolean).length;

  const popups = (
    <>
      {showSpotlight && !showFlip && <CustomerSpotlight order={showSpotlight} />}
      {showFlip && <FlipAnimation onDone={() => { setShowFlip(false); setFlipTimestamp(null); }} flippedAt={flipTimestamp} />}
      {showTipJar && !showFlip && !partyVoice && <TipJarPrompt onDone={() => setShowTipJar(false)} />}
    </>
  );

  const rootStyle = {
    width: "100vw", height: "100vh",
    background: "transparent",
    fontFamily: "system-ui, -apple-system, sans-serif",
    userSelect: "none", overflow: "hidden", position: "relative",
    color: "white",
  };

  // ── Portrait — Booth's top bar + status rail, then the griddle ──
  if (portrait) {
    return (
      <div data-orientation={orientation} style={{ ...rootStyle, display: "flex", flexDirection: "column" }}>
        <TopBar activeShift={activeShift} queueDepth={queueDepth} waitMin={waitMin} portrait />

        <StatusRow
          activeShift={activeShift}
          drawing={drawingOrder}
          cookingAll={cookingAll}
          queued={queuedOrders}
          lastDone={lastDoneOrder}
          designMap={designMap}
          portrait
        />

        {/* Griddle — transparent flex remainder; the camera sits behind.
            The ordering QR (flow-resolved SummonBanner) lives on the feed. */}
        <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
          <SummonBanner
            scene="main"
            shift={activeShift}
            concurrentCount={concurrentCount}
            streamConfig={streamConfig}
            queueDepth={queueDepth}
            waitMin={waitMin}
            portrait
          />
        </div>

        <div style={{
          flexShrink: 0, height: "72px", boxSizing: "border-box",
          background: BAR_BG,
          borderTop: `3px solid ${BRAND.gold}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: BRAND.gold, fontFamily: D, fontWeight: 900,
          fontSize: "1.8rem", letterSpacing: "0.05em",
        }}>
          ✨ thejoyofpancakes.com
        </div>

        {popups}
      </div>
    );
  }

  // ── Landscape — top bar + one right-hand card column; the rest is griddle ──
  const BAR_H = 96 + 3; // TopBar row + gold border
  const PAD = 20;
  const CARD_W = 500;
  const CARD_H = 175;
  const THUMB = 135;
  const drawTimer = drawingOrder?.startedAt ? fmtTimer(now - drawingOrder.startedAt.toMillis()) : null;
  // cookingAll is capped at 3 upstream; slice defensively so the column
  // (≤5 cards × 175px) can never run off the bottom of a 1080 canvas.
  const cooking = cookingAll.slice(0, 3);
  // Up Next — only while nothing is drawing (same rule as StatusRail).
  // Suspended orders never surface; custom uploads are unvetted until the
  // artist starts, so only flash art (ours) may render on the tile.
  const upNext = drawingOrder ? null : (queuedOrders.find((o) => o.state === "queued") ?? null);
  const upNextSrc = upNext && (upNext.orderType === "flash" || upNext.orderType === "freestyle_flash")
    ? getRefSrc(upNext, designMap) : null;
  const idle = !drawingOrder && !upNext && cooking.length === 0 && !lastDoneOrder;

  return (
    <div data-orientation={orientation} style={rootStyle}>
      <TopBar activeShift={activeShift} queueDepth={queueDepth} waitMin={waitMin} portrait={false} />

      {/* Ordering QR — over the griddle area, clear of the card column. */}
      <div style={{
        position: "absolute",
        top: BAR_H + PAD, bottom: 0, left: 0,
        right: CARD_W + PAD * 2,
      }}>
        <SummonBanner
          scene="main"
          shift={activeShift}
          concurrentCount={concurrentCount}
          streamConfig={streamConfig}
          queueDepth={queueDepth}
          waitMin={waitMin}
          portrait={false}
        />
      </div>

      <div style={{
        position: "absolute", top: BAR_H + PAD, right: PAD, bottom: PAD,
        width: `${CARD_W}px`,
        display: "flex", flexDirection: "column", gap: "12px",
        overflow: "hidden",
      }}>
        {drawingOrder && (
          <CardIn key={`draw-${drawingOrder.id}`}>
            <QueueCard
              order={drawingOrder}
              variant="active"
              slot="drawing"
              timer={drawTimer}
              timerLabel="draw time"
              mirrored={true}
              thumbPx={THUMB}
              cardH={CARD_H}
              imgSrc={getRefSrc(drawingOrder, designMap)}
            />
          </CardIn>
        )}

        {upNext && (
          <CardIn key={`next-${upNext.id}`}>
            <QueueCard
              order={upNext}
              variant="active"
              slot="drawing"
              label="⏭ Up Next"
              unvetted
              thumbPx={THUMB}
              cardH={CARD_H}
              imgSrc={upNextSrc}
            />
          </CardIn>
        )}

        {cooking.map((cOrder) => {
          const start = cOrder.cookingStartedAt || cOrder.flippedAt;
          return (
            <CardIn key={`cook-${cOrder.id}`}>
              <QueueCard
                order={cOrder}
                variant="active"
                slot="cooking"
                timer={start ? fmtTimer(now - start.toMillis()) : null}
                timerLabel="cook time"
                mirrored={false}
                thumbPx={THUMB}
                cardH={CARD_H}
                imgSrc={getRefSrc(cOrder, designMap)}
              />
            </CardIn>
          );
        })}

        {lastDoneOrder && (
          <CardIn key={`done-${lastDoneOrder.id}`}>
            <QueueCard
              order={lastDoneOrder}
              variant="done"
              thumbPx={THUMB}
              cardH={CARD_H}
            />
          </CardIn>
        )}

        {idle && <IdleCard />}
      </div>

      {popups}
    </div>
  );
}
